<template>
  <div v-if="showDocument" class="legal-document">
    <Loading v-if="loadingDocument" />
    <div v-else v-html="documentContent.content" />
  </div>
  <div v-else>
    <DisclaimerBox />
    <v-row class="mb-6">
      <v-col :cols="12" :md="6">
        <FirstName :form-name="formName" required />
      </v-col>
      <v-col :cols="12" :md="6">
        <LastName :form-name="formName" required />
      </v-col>
    </v-row>
    <Phone
      :form-name="formName"
      :show-help-text="false"
      :prefix-selected="
        phoneFormatted($auth.user.phone) ? phoneFormatted($auth.user.phone).prefix : ''
      "
    />
    <div class="mt-6 grey-darken-1--text text-body-small">
      <i18n path="videoCall.formStep.legal.personalData.text">
        <template #link>
          <span
            class="link--inline grey-darken-1--text"
            @click.prevent.stop="showLegalDocument('personal_data_processing')"
            >{{ $t('videoCall.formStep.legal.personalData.legalText') }}</span
          >
        </template>
      </i18n>
    </div>
    <div class="mt-6">
      <v-checkbox
        v-model="$v.legalAgreementTermsAndConditions.$model"
        hide-details
        :error="$v.legalAgreementTermsAndConditions.$error"
      >
        <template #label>
          <i18n path="videoTherapy.formStep.legal.termsConditions.text">
            <template #legalText>
              <span
                class="link--inline black--text"
                @click.prevent.stop="showLegalDocument('mentalhealth')"
                >{{ $t('videoCall.formStep.legal.termsConditions.legalText') }}</span
              >
            </template>
          </i18n>
        </template>
      </v-checkbox>
    </div>
    <DocumentModal
      v-for="(document, key) in legalDocuments"
      :key="key"
      identifier="videotherapy"
      :name="document.name"
      category="mentalhealth"
      :type-name="document.type"
    />
  </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { checked } from '@/customVuelidate';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import checkUserVerification from '@/mixins/checkUserVerification';
import lodash from 'lodash';
import legalDocuments from '@/mixins/legalDocuments';
import dynamicForms from '@/mixins/dynamicForms';

export default {
  name: 'VideoTherapyFormStep',
  mixins: [checkUserVerification, legalDocuments, dynamicForms],
  props: {
    formName: {
      type: String,
      required: true
    }
  },
  validations() {
    return {
      legalAgreementTermsAndConditions: { required, checked }
    };
  },
  data() {
    return {
      loading: false,
      loadingDocument: false,
      legalAgreementTermsAndConditions: false,
      showDocument: false,
      videoCallLegalType: '',
      documentContent: {
        content: ''
      },
      type: ''
    };
  },
  computed: {
    legalDocuments() {
      return [
        {
          name: 'mentalhealth',
          type: 'mentalhealth'
        },
        {
          name: 'personal_data_processing',
          type: 'personal_data_processing'
        },
        {
          name: 'terms_and_conditions',
          type: 'terms_and_conditions'
        }
      ];
    },
    ...mapGetters({
      firstName: 'forms/firstName/firstName',
      lastName: 'forms/lastName/lastName',
      phone: 'forms/phone/phone',
      isEmergencyChecked: 'forms/emergencyDisclaimer/isEmergencyChecked',
      isValidFirstName: 'forms/firstName/isValid',
      isValidLastName: 'forms/lastName/isValid',
      isValidPhone: 'forms/phone/isValid'
    })
  },
  watch: {
    loading() {
      this.setDynamicDialogButtons();
    },
    async showDocument(newValue) {
      if (newValue) {
        this.loadingDocument = true;
        this.setDialogTitle(null);
        this.documentContent = await this.getLegalDocument('videotherapy', this.type);
        this.setDialogTitle(
          this.documentContent && this.documentContent.title ? this.documentContent.title : null
        );
        this.loadingDocument = false;
      }
    },
    currentStep() {
      this.loading = false;
    }
  },
  mounted() {
    this.loading = false;
    this.setDialogTitle(this.$t('videoCall.formStep.title'));
    this.setDynamicDialogButtons();
    this.updateFirstName(this.$auth.user.firstName || '');
    this.updateLastName(this.$auth.user.lastName || '');
    if (this.$auth.user.phone) {
      this.updatePhoneNumber(this.phoneFormatted(this.$auth.user.phone).number.toString());
    }
  },
  methods: {
    showLegalDocument(type) {
      this.setClickBack(() => {
        this.setClickBack(undefined);
        this.setDynamicDialogButtons();
        this.setDialogTitle(this.$t('videoCall.formStep.title'));
        this.showDocument = false;
      });
      this.setDialogButtons([]);
      this.type = type;
      this.showDocument = true;
    },
    setDynamicDialogButtons() {
      this.setDialogButtons([
        {
          type: 'sendButton',
          props: {
            loading: this.loading
          },
          events: {
            click: () => {
              if (!this.isEmergencyChecked) {
                this.$nextTick(() => this.scrollToError());
              } else {
                this.submitFormData();
              }
            }
          },
          text: this.$t('goNext')
        }
      ]);
    },
    submitFormData() {
      return this.$eventHub.$emit('validateFirstName', () => {
        this.$eventHub.$emit('validateLastName', () => {
          this.$eventHub.$emit('validatePhone', async () => {
            await this.sentFormData();
          });
        });
      });
    },
    async sentFormData() {
      if (this.isValidFirstName && this.isValidLastName && this.isValidPhone) {
        this.$v.$touch();
        if (!this.$v.$invalid) {
          await this.updateWebUser();
        } else {
          this.$nextTick(() => this.scrollToError());
        }
      } else {
        this.$nextTick(() => this.scrollToError());
      }
    },
    async updateWebUser() {
      this.loading = true;

      if (!(await this.checkUserVerification(false, true))) {
        this.loading = false;
        return false;
      }

      let personalDataProcessing = await this.getLegalDocument(
        'videocall',
        'personal_data_processing'
      );

      let termsConditions = await this.getLegalDocument('videocall', 'terms_and_conditions');
      let mentalhealth = await this.getLegalDocument('videotherapy', 'mentalhealth');

      await this.$http.post('/v3/legal-documents/' + personalDataProcessing.id + '/confirm');
      await this.$http.post('/v3/legal-documents/' + termsConditions.id + '/confirm');
      await this.$http.post('/v3/legal-documents/' + mentalhealth.id + '/confirm');

      let updatedFields = {};
      if (this.firstName !== this.$auth.user.firstName) {
        updatedFields.firstName = this.firstName;
      }
      if (this.lastName !== this.$auth.user.lastName) {
        updatedFields.lastName = this.lastName;
      }
      if (
        (!!this.phone && `${this.phone.prefix}${this.phone.number}` !== this.$auth.user.phone) ||
        !this.phone
      ) {
        updatedFields.phone = this.phone;
      }
      if (!lodash.isEmpty(updatedFields)) {
        // TODO: migrate to patchUser helper
        await this.$http
          .patch('/v3/fe/web-users', updatedFields)
          .then(async () => {
            for (const [key] of Object.entries(updatedFields)) {
              this.gtmPush({ event: 'profile_update_success', 'profile.field': key });
            }
            await this.$auth.fetchUser();
            await this.getAllDefaultFormData();

            if (this.isFlutterReady) {
              window.flutter_inappwebview.callHandler('onProfileUpdated');
            }

            this.gtmPush({ event: 'form_sent_success', 'form.name': this.$route?.query?.dfn });
            this.$emit('next-click');
          })
          .catch(error => {
            this.$store.dispatch('errorModal/handleError', {
              error,
              showModal: false,
              code: '0x00000083'
            });
            this.loading = false;
          });
      } else {
        this.$store.commit('forms/emergencyDisclaimer/setIsEmergencyChecked', false);
        this.gtmPush({ event: 'form_sent_success', 'form.name': this.$route?.query?.dfn });
        this.$emit('next-click');
      }
    },
    ...mapMutations({
      updateFirstName: 'forms/firstName/updateFirstName',
      updateLastName: 'forms/lastName/updateLastName',
      updatePhoneNumber: 'forms/phone/updatePhoneNumber',
      updatePhoneCountry: 'forms/phone/updatePhoneCountry'
    }),
    ...mapActions({
      setDialogTitle: 'staticForms/videoCall/formStep/setDialogTitle',
      setDialogButtons: 'staticForms/videoCall/formStep/setDialogButtons',
      setClickBack: 'staticForms/videoCall/formStep/setClickBack',
      getAllDefaultFormData: 'dynamicForms/getAllDefaultFormData'
    })
  }
};
</script>
